import { AitRowGroupData } from "./AitRowGroupData";
import { AitHeaderGroupData } from "./interface";

/**
 * Remove one or more columns from the table data identified by `cellIds`.
 *
 * The function updates header rows (adjusting colSpan and revealing hidden
 * cells where necessary) and removes matching cells from each body row. It
 * verifies that row lengths remain consistent after removal and throws if they
 * are not.
 *
 * @template T - The cell payload type
 * @param headerData - Header group data or `false` when not present
 * @param bodyData - Array of body row groups
 * @param cellIds - Array of cell aitid strings identifying columns to remove
 * @returns Updated `headerData` (or `false`) and `bodyData` with specified columns removed
 * @throws Error when removing columns would leave inconsistent row lengths
 */
export const trRemoveColumn = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  cellIds: string[],
): { headerData: AitHeaderGroupData<T> | false; bodyData: AitRowGroupData<T>[] } => {
  let newHeaderData: AitHeaderGroupData<T> | false = headerData ? { ...headerData } : false;

  // Update header data
  if (newHeaderData !== false) {
    newHeaderData = {
      ...newHeaderData,
      rows: newHeaderData.rows.map((r) => {
        const newRow = { ...r };
        const newCells = [...newRow.cells];
        // Check for colSpan
        const c = newCells.find((c) => cellIds.includes(c.aitid));
        /* istanbul ignore else */
        if (c) {
          const columnIndex = newCells.indexOf(c);
          // Reduce where a hidden cell has been removed
          if (c.colSpan === 0) {
            let lookBack = 1;
            while (newCells[columnIndex - lookBack].colSpan === 0) {
              lookBack++;
            }
            newCells[columnIndex - lookBack] = {
              ...newCells[columnIndex - lookBack],
              colSpan: newCells[columnIndex - lookBack].colSpan - 1,
            };
          }
          // Reveal where an expanded cell has been removed
          else if (c.colSpan > 1) {
            for (let cj = 1; cj < c.colSpan; cj++) {
              newCells[columnIndex + cj] = {
                ...newCells[columnIndex + cj],
                colSpan: 1,
              };
            }
          }
          newCells.splice(columnIndex, 1);
        }
        return { ...newRow, cells: newCells };
      }),
    };
  }
  // Update body groups
  const newBodyData = bodyData.map((rg) => {
    const newRg = { ...rg };
    newRg.rows = rg.rows.map((r) => {
      const newRow = { ...r };
      // Remove cells that match the cellIds
      newRow.cells = newRow.cells.filter((c) => !cellIds.includes(c.aitid));
      return newRow;
    });
    return newRg;
  });
  // Check each row is the same length
  const rowLength = bodyData[0].rows[0].cells.length;
  if (
    bodyData.some((rg) => rg.rows.some((r) => r.cells.length !== rowLength)) ||
    (headerData !== false && headerData.rows.some((r) => r.cells.length !== rowLength))
  ) {
    throw new Error("Row lengths are not consistent after removing column");
  }
  return { headerData: newHeaderData, bodyData: newBodyData };
};
